import { useCallback, useEffect, useRef } from "react"
import { DATAFILE_TYPE } from "types/dataImport.types"
import { useEngine } from "hooks/api/useEngine"
import { useDataImport } from "features/DataImport/api/useDataImport"
import { useProjectStore } from "stores/projectStore"
import handleSequenceError from "utils/handleSequenceError"
import type {
  EngineQueryResult,
  HierarchicalDataEntry,
} from "types/engineOutput.types"
import {
  readInitialiseReport,
  runOnLoadFailureText,
} from "types/runOnLoad.types"
import { useDashboardStoreApi } from "./dashboardStore"
import { extractEngineError } from "./engineRunner"
import {
  numericVariables,
  PrepPipeline,
  prepParts,
  weightsLine,
} from "./prepScript"

const messageOf = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback

const NO_PIPELINES: PrepPipeline[] = []

/** The preparation's error while the list of saved pipelines failed to load. */
export const PIPELINES_NOT_LOADED = "Could not load the pipelines"

/**
 * Owns the engine session for one mounted dashboard: initialise-data once,
 * then RESET, the preparation, and CODEBOOK. The preparation comes from the
 * document (its pipelines, weight and Advanced script) with `pipelines`,
 * the project's saved pipelines; null while they load, which holds the
 * first start back so it does not run without them. `pipelinesFailed` says
 * the list could not be loaded. A failed preparation leaves the session
 * open in "prep-error" with the codebook loaded, so the dashboard stays
 * usable for fixing it. The preparation does not run at all without every
 * pipeline it applies: an applied pipeline the list does not have, or a
 * list that failed to load, is a failed preparation too. `restart` re-runs
 * the RESET sequence on the same instance (preparation edits, codebook
 * refresh, the list loading again) and bumps the session generation, which
 * gives the tile runner a fresh queue and cache.
 */
export const useDashboardSession = (
  pipelines: PrepPipeline[] | null = NO_PIPELINES,
  pipelinesFailed = false
): { restart: () => Promise<void> } => {
  const store = useDashboardStoreApi()
  const project = useProjectStore((s) => s.project)
  const { initialiseData, RunEngineQuery } = useEngine()
  const { getDataFilesUploaded } = useDataImport()
  const startedFor = useRef<string | null>(null)
  /** Bumped by every RESET sequence; an older one that is still awaiting stops writing. */
  const sequence = useRef(0)
  /** The latest pipelines, read when a RESET is sent. */
  const pipelinesRef = useRef(pipelines)
  const pipelinesFailedRef = useRef(pipelinesFailed)
  /** The scripts the Conductor ran on load into the instance: the preparation skips them. */
  const onLoadRef = useRef<string[]>([])
  useEffect(() => {
    pipelinesRef.current = pipelines
    pipelinesFailedRef.current = pipelinesFailed
  }, [pipelines, pipelinesFailed])

  const resetAndCodebook = useCallback(
    async (instance: string): Promise<void> => {
      const mine = ++sequence.current
      const overtaken = (): boolean => sequence.current !== mine
      const {
        document,
        setSession,
        setCodebook,
        resetKnownFilterFields,
        markAllStale,
      } = store.getState()
      try {
        const reset = await RunEngineQuery(instance, "RESET", false)
        if (overtaken()) return
        const resetError = extractEngineError(reset.data)
        if (resetError) {
          setSession({
            status: "error",
            error: `The data could not be reset: ${resetError}`,
          })
          return
        }
        // The preparation runs after the RESET, part by part, so a failure
        // names its pipeline or line and never keeps the codebook from
        // loading. It stops at the first failing part. Without the list of
        // pipelines, or with an applied pipeline the project no longer has,
        // it does not run at all: the tiles would show unprepared data.
        const chosen = document.data.pipelines
        const { parts, missing } = prepParts({
          pipelines: pipelinesRef.current ?? NO_PIPELINES,
          chosen,
          weight: document.data.weight,
          advanced: document.prepScript,
          onLoad: onLoadRef.current,
        })
        let prepError: string | null =
          chosen.length > 0 && pipelinesFailedRef.current
            ? PIPELINES_NOT_LOADED
            : missing.length > 0
              ? `Pipeline "${missing[0]}" not found`
              : null
        for (const part of prepError ? [] : parts) {
          const ran = await RunEngineQuery(instance, part.script, false)
          if (overtaken()) return
          const failed = extractEngineError(ran.data)
          if (failed) {
            prepError = `${part.label}: ${failed}`
            break
          }
        }
        const cb = await RunEngineQuery(instance, "CODEBOOK", false)
        if (overtaken()) return
        if (!handleSequenceError(cb.data, "Error creating codebook")) {
          setSession({ status: "error", error: "Could not load the codebook." })
          return
        }
        const output = (cb.data as EngineQueryResult).result?.output?.[0]
        const value =
          output && output.content && "value" in output.content
            ? (output.content as { value: unknown }).value
            : null
        const codebook = Array.isArray(value)
          ? (value as HierarchicalDataEntry[])
          : null
        setCodebook(codebook)
        // The engine takes any name in USEWEIGHTS and fails only in the
        // tables that use it, so the weight is checked against the data here.
        const weight = document.data.weight
        if (
          !prepError &&
          weight &&
          codebook &&
          !numericVariables(codebook).includes(weight)
        ) {
          prepError = `${weightsLine(weight)}: ${weight} is not a numeric variable in the data.`
        }
        // The RESET dropped every universe field; this also bumps the
        // generation so results cached before it are not reused.
        resetKnownFilterFields()
        setSession(
          prepError
            ? { status: "prep-error", error: undefined, prepError, preparation: parts }
            : { status: "ready", error: undefined, prepError: undefined, preparation: parts }
        )
        // Stale tiles wait: none runs while the preparation has failed.
        markAllStale()
      } catch (e) {
        if (overtaken()) return
        setSession({
          status: "error",
          error: messageOf(e, "Could not prepare the data."),
        })
      }
    },
    [store, RunEngineQuery]
  )

  const start = useCallback(async (): Promise<void> => {
    const { setSession } = store.getState()
    setSession({ status: "starting", error: undefined, prepError: undefined })
    try {
      const size = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )
      if (typeof size.data !== "number") {
        setSession({
          status: "error",
          error: "This project has no data file. Import data first.",
        })
        return
      }
      const init = await initialiseData(project.id, "report")
      const report = readInitialiseReport(init.data)
      if (!report) {
        handleSequenceError(init.data, "Error initialising data")
        setSession({ status: "error", error: "The engine could not open the project data." })
        return
      }
      if (report.failed) {
        setSession({ status: "error", error: runOnLoadFailureText(report.failed) })
        return
      }
      onLoadRef.current = report.applied
      const instance = report.instance
      setSession({ instance })
      await resetAndCodebook(instance)
    } catch (e) {
      setSession({
        status: "error",
        error: messageOf(e, "Could not start the session."),
      })
    }
  }, [
    store,
    project.id,
    project.Created_by,
    getDataFilesUploaded,
    initialiseData,
    resetAndCodebook,
  ])

  // One initialise-data per mounted dashboard: every call creates an engine
  // instance holding a copy of the data.
  const pipelinesLoaded = pipelines !== null
  useEffect(() => {
    if (!project.id || !pipelinesLoaded || startedFor.current === project.id)
      return
    startedFor.current = project.id
    void start()
  }, [project.id, pipelinesLoaded, start])

  const restart = useCallback(async (): Promise<void> => {
    const { session, setSession } = store.getState()
    if (!session.instance) {
      // A start still in flight would otherwise open a second instance; one
      // still waiting for the pipelines starts once they are in.
      if (session.status === "starting" || pipelinesRef.current === null)
        return
      return start()
    }
    setSession({ status: "starting", error: undefined, prepError: undefined })
    await resetAndCodebook(session.instance)
  }, [store, start, resetAndCodebook])

  return { restart }
}
